import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { istFehlendesSchema } from './sifaService';
import { alsObjekt, bereinigeMitarbeiterEingabe, mapKundenportalDaten, mapPortalMitarbeiter, mapStatus } from './kundenportalLogic';
import type {
  KundenportalDaten,
  KundenportalStatus,
  PortalDateiArt,
  PortalMitarbeiter,
  PortalMitarbeiterEingabe,
} from './kundenportalTypes';

/**
 * Die ANONYME Kundenseite `#/kundenportal/<token>` — nur RPCs, keine Tabelle
 * (docs/sifa-unterweisung-plan.md §0, Muster sifaPublicService.ts). `anon`
 * hat auf keiner `sifa_*`-Tabelle Rechte; alles laeuft ueber die
 * security-definer-Funktionen aus supabase/sifa_kundenportal_rpc.sql (Nr. 94),
 * die Token UND PIN pruefen und den Fehlversuchszaehler fuehren.
 *
 * RPC-Namen und Parameter EXAKT wie in Nr. 94. Ein umbenannter Parameter
 * bricht die Seite still (PGRST202) — genau das faengt `istFehlendesSchema`
 * als Status 'schema' ab.
 *
 * Fehlstatus kommen NICHT als Exception, sondern als `{ ok: false, fehler }`
 * (die RPCs werfen bewusst nicht, sonst rollte der Zaehler zurueck). Nur
 * echte Fehler (Netz, Server) werfen — als Error mit Klartext.
 */

export type LadeErgebnis = { ok: true; daten: KundenportalDaten } | KundenportalStatus;
export type SpeichernErgebnis = { ok: true; mitarbeiter: PortalMitarbeiter } | KundenportalStatus;
export type DateiErgebnis = { ok: true; url: string; dateiname: string } | KundenportalStatus;

const werfe = (e: unknown): never => {
  throw new Error(describeError(e));
};

const formGueltig = (token: string, pin: string): boolean => token.length >= 32 && /^[0-9]{6}$/.test(pin);

/** Alles fuer die Kundenseite in einem Aufruf (RPC `sifa_kundenportal_laden`). */
export const ladeKundenportal = async (token: string, pin: string): Promise<LadeErgebnis> => {
  const t = (token || '').trim();
  const p = (pin || '').trim();
  if (!t) return { ok: false, fehler: 'unbekannt' };
  if (!formGueltig(t, p)) return { ok: false, fehler: t.length < 32 ? 'unbekannt' : 'pin', versucheRest: null };
  try {
    const { data, error } = await supabase.rpc('sifa_kundenportal_laden', { p_token: t, p_pin: p });
    if (error) throw error;
    const row = alsObjekt(data);
    if (!row) return { ok: false, fehler: 'unbekannt' };
    if (row.ok !== true) return mapStatus(row);
    return { ok: true, daten: mapKundenportalDaten(row) };
  } catch (e) {
    if (istFehlendesSchema(e)) return { ok: false, fehler: 'schema' };
    return werfe(e);
  }
};

/** Person anlegen/aendern (RPC `sifa_kundenportal_mitarbeiter_speichern`) — nur die erlaubten Felder. */
export const speichereMitarbeiterPortal = async (
  token: string,
  pin: string,
  eingabe: PortalMitarbeiterEingabe,
): Promise<SpeichernErgebnis> => {
  const t = (token || '').trim();
  const p = (pin || '').trim();
  if (!formGueltig(t, p)) return { ok: false, fehler: 'unbekannt' };
  try {
    const { data, error } = await supabase.rpc('sifa_kundenportal_mitarbeiter_speichern', {
      p_token: t,
      p_pin: p,
      p_mitarbeiter: bereinigeMitarbeiterEingabe(eingabe),
    });
    if (error) throw error;
    const row = alsObjekt(data);
    if (!row) return { ok: false, fehler: 'eingabe', text: 'Der Server hat nicht geantwortet.' };
    if (row.ok !== true) return mapStatus(row);
    const m = alsObjekt(row.mitarbeiter);
    if (!m) return { ok: false, fehler: 'eingabe', text: 'Die Person kam nicht zurück.' };
    return { ok: true, mitarbeiter: mapPortalMitarbeiter(m) };
  } catch (e) {
    if (istFehlendesSchema(e)) return { ok: false, fehler: 'schema' };
    return werfe(e);
  }
};

/**
 * Signierte Download-URL (60 s) ueber den Server-Endpunkt
 * `POST /api/kundenportal-datei` — der einzige Weg an eine Datei: der Server
 * prueft Token+PIN ueber `sifa_kundenportal_datei_pruefen` (nur service_role)
 * und signiert mit dem Service-Role-Key. Der Browser sieht nie einen
 * Storage-Pfad.
 */
export const holeDateiUrl = async (token: string, pin: string, art: PortalDateiArt, id: string): Promise<DateiErgebnis> => {
  const t = (token || '').trim();
  const p = (pin || '').trim();
  if (!formGueltig(t, p)) return { ok: false, fehler: 'unbekannt' };
  let antwort: Response;
  try {
    antwort = await fetch('/api/kundenportal-datei', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: t, pin: p, art, id }),
    });
  } catch (e) {
    return { ok: false, fehler: 'netz', text: `Der Server ist nicht erreichbar (${describeError(e)}).` };
  }
  let body: Record<string, unknown> = {};
  try {
    body = (await antwort.json()) as Record<string, unknown>;
  } catch {
    body = {};
  }
  if (antwort.ok && typeof body.url === 'string' && body.url) {
    return { ok: true, url: body.url, dateiname: typeof body.dateiname === 'string' ? body.dateiname : 'Datei' };
  }
  if (typeof body.fehler === 'string') {
    return mapStatus({ ...body, text: typeof body.error === 'string' ? body.error : null });
  }
  return {
    ok: false,
    fehler: 'netz',
    text: typeof body.error === 'string' && body.error ? body.error : `Der Download wurde abgelehnt (HTTP ${antwort.status}).`,
  };
};
